import { useEffect, useState } from "react";
import {
  userData,
  selectedOptic,
  exportableFieldsPatient,
  exportDataPatient,
} from "../js/utils";
import { download, post } from "../js/post";
import moment from "moment";
import "moment/locale/es";
import { TabSelector } from "./TabSelector";
import PopupMessage from "./PopupMessage";
import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import Tag from "./Tag";
import { DashboardSummaryBox } from "./DashboardSummaryBox";
import { DashboardPatientPanelFilterSection } from "./DashboardPatientPanelFilterSection";
import { DashboardPatientPanelFileFilterSection } from "./DashboardPatientPanelFileFilterSection";
import { DashboardPatientPanelPurchaseFilterSection } from "./DashboardPatientPanelPurchaseFilterSection";
import { DashboardStorePanelFilterSectionExportableFields } from "./DashboardStorePanelFilterSectionExportableFields";
import { DashboardStorePanelFilterSectionExport } from "./DashboardStorePanelFilterSectionExport";
import { DashboardPatientStatsPanel } from "./DashboardPatientStatsPanel";
import { DashboardPatientChartsPanel } from "./DashboardPatientCharts";
import LoadingScreen from "./LoadingScreen";
import { useOptics } from "../hooks/useOptics";

export function DashboardPatientPanel({
  createPopupQuestion,
  removePopupQuestion,
}) {
  moment.locale("es");

  const opticalList = useOptics();
  const [selectedOpticalList, setSelectedOpticalList] = useState([]);
  const [searchYear, setSearchYear] = useState(moment());
  const [selectedPanelView, setSelectedPanelView] = useState("Listados");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingEmailMessage, setSendingEmailMessage] = useState("");
  const [sendingEmailMessageDef, setSendingEmailMessageDef] = useState("");
  const [exportData, setExportData] = useState(exportDataPatient);
  const [healthReport, setHealthReport] = useState(0);
  const [demographicReport, setDemographicReport] = useState({
    averageAge: 0,
    malePercentage: 0,
    femalePercentage: 0,
  });
  const [activityReport, setActivityReport] = useState(0);

  const getOpticalListIds = () => {
    return opticalList
      .filter((e) => selectedOpticalList.includes(e.text))
      .map((e) => e.id);
  };

  const getHealthReport = async () => {
    const response = await post(
      "support-extra/dashboard/ObtenerInformeSaludClientesDashboard.php",
      {
        token: userData.token,
        opticas: getOpticalListIds(),
      },
    );
    setHealthReport(response.porcentaje);
  };

  const getDemographicReport = async () => {
    const response = await post(
      "support-extra/dashboard/ObtenerInformeDemograficoClientesDashboard.php",
      {
        token: userData.token,
        opticas: getOpticalListIds(),
      },
    );
    setDemographicReport({
      averageAge: response.mediaEdad,
      malePercentage: response.porcentajeMasculino,
      femalePercentage: response.porcentajeFemenino,
    });
  };

  const getActivityReport = async () => {
    const response = await post(
      "support-extra/dashboard/ObtenerInformeActividadClientesDashboard.php",
      {
        token: userData.token,
        opticas: getOpticalListIds(),
        ano: searchYear.year(),
      },
    );
    setActivityReport(response.nuevosPacientes);
  };

  const getClientHealthReportDashboardViewMore = async () => {
    await download(
      "support-extra/dashboard/ObtenerInformeSaludClientesDashboardVerMas.php",
      {
        token: userData.token,
        opticas: getOpticalListIds(),
      },
      { abrirEnPestana: true },
    );
  };

  const getClientDemographicReportDashboardViewMore = async () => {
    await download(
      "support-extra/dashboard/ObtenerInformeDemograficoClientesDashboardVerMas.php",
      {
        token: userData.token,
        opticas: getOpticalListIds(),
      },
      { abrirEnPestana: true },
    );
  };

  const getClientActivityReportDashboardViewMore = async () => {
    await download(
      "support-extra/dashboard/ObtenerInformeActividadClientesDashboardVerMas.php",
      {
        token: userData.token,
        opticas: getOpticalListIds(),
        ano: searchYear.year(),
      },
      // Informe siempre en PDF: se previsualiza en una pestaña
      { abrirEnPestana: true },
    );
  };

  async function exportFile(email) {
    if (exportData.datosExportacion.length === 0) {
      createPopupQuestion(
        "Debe seleccionar al menos un campo exportable.",
        () => {
          removePopupQuestion();
          return;
        },
        null,
        "Entendido",
      );
    } else {
      const exportDatAux = {
        token: userData.token,
        ...exportData,
        email: !email ? "" : exportData.email,
        idOpticaEmail: !email ? "" : selectedOptic.id,
      };
      if (!email) {
        setSendingEmailMessageDef(
          <LoadingScreen message="Generando archivo..."></LoadingScreen>,
        );
        setSendingEmail(true);
        const response = await download(
          "support-extra/dashboard/ExportacionClientes.php",
          exportDatAux,
          // Los PDF se previsualizan en el navegador; Excel/CSV se descargan
          { abrirEnPestana: exportData.tipoFichero === 0 },
        );
        if (response.error !== "") {
          setSendingEmailMessage(response.error);
        } else setSendingEmail(false);
      } else {
        setSendingEmailMessageDef(
          <LoadingScreen message="Enviando archivo..."></LoadingScreen>,
        );
        setSendingEmail(true);
        const response = await post(
          "support-extra/dashboard/ExportacionClientes.php",
          exportDatAux,
        );
        if (response.error !== "") {
          setSendingEmailMessage(response.error);
        } else {
          setSendingEmailMessage("El email se ha enviado correctamente.");
        }
      }
    }
  }

  useEffect(() => {
    getHealthReport();
    getDemographicReport();
    getActivityReport();
  }, [selectedOpticalList]);

  useEffect(() => {
    getActivityReport();
  }, [selectedOpticalList, searchYear]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 20,
      }}
    >
      <div className="patient-search-content dashboard-bg">
        <div className="opt-table-title">Análisis de pacientes</div>
        <div
          className="dashboard-content-data"
          style={{ alignItems: "center" }}
        >
          {sendingEmail && (
            <PopupMessage
              text={
                sendingEmailMessage === ""
                  ? sendingEmailMessageDef
                  : sendingEmailMessage
              }
              textAccept={sendingEmailMessage === "" ? null : "Entendido"}
              onClickAccept={
                sendingEmailMessage === ""
                  ? null
                  : () => {
                      setSendingEmailMessage("");
                      setSendingEmailMessageDef("");
                      setSendingEmail(false);
                    }
              }
            ></PopupMessage>
          )}
          <div
            className="thin-rounded-border dashboard-analysis-box"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div className="data-field">
              <span className="label">Ópticas</span>
              <InputIconSearchPanelMultiSelection
                size={3}
                type={"type-alphanum-ext"}
                options={opticalList.map((e) => e.text)}
                selectedOptions={selectedOpticalList}
                onChange={(e) => {
                  setSelectedOpticalList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedOpticalList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Ópticas seleccionadas: {selectedOpticalList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedOpticalList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedOpticalList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                justifyContent: "space-evenly",
              }}
            >
              <DashboardSummaryBox
                style={{ marginTop: 30 }}
                title="Informe de salud de la BD"
                icon={"hearth-white.svg"}
                text={`${healthReport} %`}
                onClick={getClientHealthReportDashboardViewMore}
              ></DashboardSummaryBox>
              <DashboardSummaryBox
                style={{ marginTop: 30 }}
                title="Informe de demografía de la BD"
                icon={"people-white.svg"}
                text={
                  demographicReport.averageAge === ""
                    ? "Sin datos"
                    : `Media de edad ${demographicReport.averageAge} años\n ${demographicReport.malePercentage} % ♂ ${demographicReport.femalePercentage} % ♀`
                }
                onClick={getClientDemographicReportDashboardViewMore}
              ></DashboardSummaryBox>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  alignItems: "flex-start",
                }}
              >
                <div className="data-field">
                  <button
                    className="incdec-btn"
                    tabIndex={-1}
                    onClick={() => {
                      setSearchYear((prev) => moment(prev).subtract(1, "year"));
                    }}
                  >
                    {"<"}
                  </button>
                  <span style={{ width: "60px", textAlign: "center" }}>
                    {moment(searchYear).year()}
                  </span>
                  <button
                    className="incdec-btn"
                    tabIndex={-1}
                    onClick={() => {
                      setSearchYear((prev) => moment(prev).add(1, "year"));
                    }}
                  >
                    {">"}
                  </button>
                </div>
                <DashboardSummaryBox
                  title="Informe de actividad de la BD"
                  icon={"patient-file-white.svg"}
                  text={`${activityReport}\n pacientes nuevos`}
                  onClick={getClientActivityReportDashboardViewMore}
                ></DashboardSummaryBox>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="patient-search-content dashboard-bg">
        <div className="opt-table-title">
          Listados y estadísticas de pacientes
        </div>
        <div
          className="dashboard-content-data"
          style={{ alignItems: "center" }}
        >
          <div className="dashboard-content-panel">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <TabSelector
                options={["Listados", "Estadísticas", "Gráficos"]}
                style={{ borderBottom: "none" }}
                selected={selectedPanelView}
                onChange={(e) => {
                  setSelectedPanelView(e);
                }}
              />
            </div>
            <div className="thin-section-rounded-border">
              {/* Ambas pestañas permanecen montadas para conservar su estado */}
              <div
                style={{
                  display: selectedPanelView === "Listados" ? "flex" : "none",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 20,
                  width: "100%",
                }}
              >
                <DashboardPatientPanelFilterSection
                  onChange={(e) => {
                    setExportData((prev) => ({
                      ...prev,
                      filtrosGenerales: e,
                    }));
                  }}
                />
                <DashboardPatientPanelFileFilterSection
                  createPopupQuestion={createPopupQuestion}
                  removePopupQuestion={removePopupQuestion}
                  onChange={(e) => {
                    setExportData((prev) => ({
                      ...prev,
                      filtrosFicha: e,
                    }));
                  }}
                />
                <DashboardPatientPanelPurchaseFilterSection
                  onChange={(e) => {
                    setExportData((prev) => ({
                      ...prev,
                      filtrosCompra: e,
                    }));
                  }}
                />
                <DashboardStorePanelFilterSectionExportableFields
                  exportableFields={exportableFieldsPatient}
                  typeId={0}
                  onChange={(e) => {
                    setExportData((prev) => ({
                      ...prev,
                      datosExportacion: e,
                    }));
                  }}
                />
                <DashboardStorePanelFilterSectionExport
                  createPopupQuestion={createPopupQuestion}
                  removePopupQuestion={removePopupQuestion}
                  onExport={exportFile}
                  onChange={(fileType, email) => {
                    setExportData((prev) => ({
                      ...prev,
                      tipoFichero: fileType,
                      email: email,
                    }));
                  }}
                />
              </div>
              <div
                style={{
                  display:
                    selectedPanelView === "Estadísticas" ? "block" : "none",
                }}
              >
                <DashboardPatientStatsPanel />
              </div>
              <div
                style={{
                  display: selectedPanelView === "Gráficos" ? "block" : "none",
                }}
              >
                <DashboardPatientChartsPanel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
